import type { LookupServiceProviderType, LookupServiceResponseType } from "@/types";
import type { LookupServiceInterface } from "@/interfaces/LookupServiceInterface";
import { lookupsLogger } from "@/utils/loggers";
import { ProviderUnavailableError, isLookupNetworkError } from "@/utils/errors/LookupServiceErrors";

import HamDbService from "./HamDbService";
import HamQthService from "./HamQthService";
import QrzService from "./QrzService";
import OfflineLookupService from "./OfflineLookupService";

type ProviderConstructor = new () => LookupServiceInterface;

const providerConstructors: [LookupServiceProviderType, ProviderConstructor][] = [
  ["HAMDB", HamDbService],
  ["HAMQTH", HamQthService],
  ["QRZ", QrzService],
  ["OFFLINE", OfflineLookupService],
];

class LookupService implements LookupServiceInterface {
  public adapter: LookupServiceInterface;
  private static providerMap = new Map(providerConstructors);

  constructor(providerName: LookupServiceProviderType) {
    if (!providerName) {
      throw new Error("Service type must be provided");
    }
    this.adapter = this.findAdapter(providerName);
  }

  // Whether an online adapter exists for this provider. Lets callers branch
  // before constructing, so a real construction failure isn't mistaken for an
  // unsupported (e.g. OFFLINE) provider.
  static isSupported(provider: LookupServiceProviderType): boolean {
    return LookupService.providerMap.has(provider);
  }

  get requiresAuthentication(): boolean {
    return this.adapter.requiresAuthentication;
  }
  get providerName(): LookupServiceProviderType {
    return this.adapter.providerName;
  }
  get providesGridsquares(): boolean {
    return this.adapter.providesGridsquares;
  }
  get requiresConnectivity(): boolean {
    return this.adapter.requiresConnectivity;
  }

  private findAdapter(
    providerName: LookupServiceProviderType
  ): LookupServiceInterface {
    const provider = LookupService.providerMap.get(providerName);
    if (!provider) {
      throw new Error(`Service ${providerName} not found`);
    }
    return new provider();
  }

  public async lookupCallsign(
    callsign: string,
    sessionKey?: string
  ): Promise<LookupServiceResponseType> {
    try {
      const result = await this.adapter.lookupCallsign(callsign, sessionKey);
      return result;
    } catch (error) {
      // A failed fetch is offline/unreachable, not a bug. Convert it to a typed
      // error the UI can surface, so it doesn't leak to Sentry as a raw TypeError.
      if (isLookupNetworkError(error)) {
        throw new ProviderUnavailableError(this.adapter.providerName);
      }
      throw error;
    }
  }

  public async authenticate(
    username: string,
    password: string
  ): Promise<string> {
    if (!this.adapter.requiresAuthentication || !this.adapter.authenticate) {
      throw new Error(
        `Service ${this.adapter.providerName} does not require authentication`
      );
    }
    try {
      lookupsLogger("Authenticating with", this.adapter.providerName);
      const sessionKey = await this.adapter.authenticate(username, password);
      return sessionKey;
    } catch (error) {
      // Treat a failed fetch during auth as an unreachable service, not a bug.
      if (isLookupNetworkError(error)) {
        throw new ProviderUnavailableError(this.adapter.providerName);
      }
      throw error;
    }
  }

  async validateCredentials(
    username: string,
    password: string
  ): Promise<{ isValid: boolean; isSet: boolean }> {
    const isSet = username !== "" && password !== "";

    if (!isSet) {
      return { isValid: false, isSet };
    }

    try {
      lookupsLogger("Validating credentials for", this.adapter.providerName);
      await this.authenticate(username, password);
      return { isValid: true, isSet };
    } catch (error) {
      return { isValid: false, isSet };
    }
  }
}

export default LookupService;
